import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';

/**
 * Valores genéricos del preview (FP-31). Son los mismos que trae
 * `index.html` para las páginas que se renderizan en el navegador: si se
 * cambian acá, cambiarlos también allá.
 */
const TITULO_POR_DEFECTO = 'Phindi — Buscador de propiedades';
const DESCRIPCION_POR_DEFECTO = 'Encontrá tu propiedad en Phindi.';
const IMAGEN = '/logos/og-phindi.jpg';

/** Largo máximo de `og:description`: WhatsApp corta en ~2 líneas igual. */
const LARGO_MAX_DESCRIPCION = 200;

/**
 * Metadatos Open Graph para compartir una página (FP-31): título,
 * descripción, imagen y URL del preview de WhatsApp/Facebook.
 *
 * Sólo tiene efecto en el preview cuando la página se renderiza en el
 * servidor (`app.routes.server.ts`): los crawlers no ejecutan JavaScript.
 * La imagen es siempre la genérica de Phindi (definición del cliente).
 *
 * `aplicar` además agrega el `<link rel="canonical">` de la página y
 * `restablecer` lo quita: sólo la ficha lo declara.
 */
@Injectable({ providedIn: 'root' })
export class OpenGraphService {
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  aplicar(titulo: string, descripcion?: string | null): void {
    const url = this.escribir(
      titulo,
      recortarDescripcion(descripcion) ?? DESCRIPCION_POR_DEFECTO,
    );
    this.linkCanonical().setAttribute('href', url);
  }

  restablecer(): void {
    this.escribir(TITULO_POR_DEFECTO, DESCRIPCION_POR_DEFECTO);
    this.document.head
      .querySelectorAll('link[rel="canonical"]')
      .forEach((link) => link.remove());
  }

  /** Escribe los metadatos y devuelve la URL canónica que usó en `og:url`. */
  private escribir(titulo: string, descripcion: string): string {
    // Los crawlers exigen URLs absolutas. Se arman con el dominio del request
    // —en el servidor `location` es la URL pedida— en vez de fijar uno: la
    // misma imagen Docker se promueve entre entornos.
    const url = urlCanonica(this.document.location.href);
    const imagen = new URL(IMAGEN, url).toString();

    const etiquetas: [string, string][] = [
      ['og:title', titulo],
      ['og:description', descripcion],
      ['og:image', imagen],
      ['og:url', url],
    ];
    for (const [property, content] of etiquetas) {
      this.meta.updateTag({ property, content });
    }

    this.meta.updateTag({ name: 'twitter:title', content: titulo });
    this.meta.updateTag({ name: 'twitter:description', content: descripcion });
    this.meta.updateTag({ name: 'twitter:image', content: imagen });
    return url;
  }

  /**
   * El único `<link rel="canonical">` del `<head>`: lo reusa si ya está (otra
   * ficha, o el que trae el HTML renderizado en el servidor al hidratar) y si
   * no lo crea. Con `DOCUMENT` y no con el `document` global: también corre
   * en el servidor.
   */
  private linkCanonical(): HTMLLinkElement {
    const head = this.document.head;
    const existente = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (existente) return existente;

    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    head.appendChild(link);
    return link;
  }
}

/**
 * URL de la página sin query string ni hash. El Backoffice comparte la ficha
 * con `?sc` (FE-126) y esa variante —o una con `utm_*`— no es otra página
 * para los buscadores ni para el preview.
 */
function urlCanonica(href: string): string {
  const url = new URL(href);
  return `${url.origin}${url.pathname}`;
}

/**
 * Descripción en una línea y cortada en una palabra, o `null` si viene vacía.
 * La de la ficha es texto libre del broker: puede traer saltos de línea y ser
 * larguísima.
 */
function recortarDescripcion(texto?: string | null): string | null {
  const plano = (texto ?? '').replace(/\s+/g, ' ').trim();
  if (!plano) return null;
  if (plano.length <= LARGO_MAX_DESCRIPCION) return plano;

  const corte = plano.slice(0, LARGO_MAX_DESCRIPCION);
  const ultimoEspacio = corte.lastIndexOf(' ');
  return `${(ultimoEspacio > 0 ? corte.slice(0, ultimoEspacio) : corte).trimEnd()}…`;
}
